import { z } from 'zod';

// Configuration categories
export type ConfigurationCategory = 
  | "financial"
  | "membership"
  | "facility_coach"
  | "operations";

// Time periods for applicable settings
export type TimePeriod = 
  | "daily"
  | "weekly"
  | "monthly"
  | "6months"
  | "yearly";

// Threshold colors
export type ThresholdColor = "red" | "amber" | "green";

// Field types
export type FieldType = "number" | "percentage" | "days" | "weeks" | "quantity" | "pkr";

// Configuration field definition
export interface ConfigField {
  key: string;
  label: string;
  type: FieldType;
  unit: string;
  category: ConfigurationCategory;
  subcategory?: string;
  timePeriod?: TimePeriod;
  validation: {
    required: boolean;
    min: number;
    max?: number;
    step?: number;
  };
}

// Configuration section
export interface ConfigSection {
  title: string;
  category: ConfigurationCategory;
  subcategory?: string;
  fields: ConfigField[];
}

// Form values
export interface DashboardConfigValues {
  [key: string]: number | string;
}

// API response
export interface DashboardConfigResponse {
  dashboard_configuration: Record<string, string>;
}

// Validation schemas with safe null/undefined/empty string support
const numberSchema = z
  .union([z.string(), z.number()])
  .optional()
  .nullable()
  .refine((val) => {
    if (val === '' || val === undefined || val === null) return true;
    const num = Number(val);
    return !isNaN(num) && num > 0;
  }, 'Must be greater than 0')
  .transform((val) => (val !== undefined && val !== null ? String(val) : ''));

const percentageSchema = z
  .union([z.string(), z.number()])
  .optional()
  .nullable()
  .refine((val) => {
    if (val === '' || val === undefined || val === null) return true;
    const num = Number(val);
    return !isNaN(num) && num > 0 && num <= 100;
  }, 'Must be greater than 0 and at most 100')
  .transform((val) => (val !== undefined && val !== null ? String(val) : ''));

const positiveIntegerSchema = z
  .union([z.string(), z.number()])
  .optional()
  .nullable()
  .refine((val) => {
    if (val === '' || val === undefined || val === null) return true;
    const num = Number(val);
    return !isNaN(num) && num > 0 && Number.isInteger(num);
  }, 'Must be a positive integer')
  .transform((val) => (val !== undefined && val !== null ? String(val) : ''));

export const financialConfigSchema = z.object({
  // Financial - Revenue Targets (all PKR)
  financial_memberships_revenue_target_daily: numberSchema,
  financial_memberships_revenue_target_weekly: numberSchema,
  financial_memberships_revenue_target_monthly: numberSchema,
  financial_memberships_revenue_target_6months: numberSchema,
  financial_memberships_revenue_target_yearly: numberSchema,
  
  financial_facility_bookings_revenue_target_daily: numberSchema,
  financial_facility_bookings_revenue_target_weekly: numberSchema,
  financial_facility_bookings_revenue_target_monthly: numberSchema,
  financial_facility_bookings_revenue_target_6months: numberSchema,
  financial_facility_bookings_revenue_target_yearly: numberSchema,
  
  financial_products_revenue_target_daily: numberSchema,
  financial_products_revenue_target_weekly: numberSchema,
  financial_products_revenue_target_monthly: numberSchema,
  financial_products_revenue_target_6months: numberSchema,
  financial_products_revenue_target_yearly: numberSchema,
  
  financial_credits_revenue_target_daily: numberSchema,
  financial_credits_revenue_target_weekly: numberSchema,
  financial_credits_revenue_target_monthly: numberSchema,
  financial_credits_revenue_target_6months: numberSchema,
  financial_credits_revenue_target_yearly: numberSchema,
  
  financial_training_revenue_target_daily: numberSchema,
  financial_training_revenue_target_weekly: numberSchema,
  financial_training_revenue_target_monthly: numberSchema,
  financial_training_revenue_target_6months: numberSchema,
  financial_training_revenue_target_yearly: numberSchema,
  
  // Financial - Large Refund
  financial_large_refund_threshold: numberSchema,
});

export const membershipConfigSchema = z.object({
  membership_new_member_target_daily: numberSchema,
  membership_new_member_target_weekly: numberSchema,
  membership_new_member_target_monthly: numberSchema,
  membership_new_member_target_6months: numberSchema,
  membership_new_member_target_yearly: numberSchema,
  membership_churn_rate_threshold: percentageSchema,
  membership_expiry_window_1: positiveIntegerSchema,
  membership_expiry_window_2: positiveIntegerSchema,
  membership_expiry_window_3: positiveIntegerSchema,
});

export const facilityCoachConfigSchema = z.object({
  facility_coach_low_utilisation_threshold: percentageSchema,
  facility_coach_facility_utilisation_red_cutoff: percentageSchema,
  facility_coach_facility_utilisation_amber_cutoff: percentageSchema,
  facility_coach_facility_utilisation_green_cutoff: percentageSchema,
  facility_coach_coach_utilisation_red_cutoff: percentageSchema,
  facility_coach_coach_utilisation_amber_cutoff: percentageSchema,
  facility_coach_coach_utilisation_green_cutoff: percentageSchema,
  facility_coach_retention_red_cutoff: percentageSchema,
  facility_coach_retention_amber_cutoff: percentageSchema,
  facility_coach_retention_green_cutoff: percentageSchema,
});

export const operationsConfigSchema = z.object({
  operations_peak_hour_occupancy_red_cutoff: percentageSchema,
  operations_peak_hour_occupancy_amber_cutoff: percentageSchema,
  operations_peak_hour_occupancy_green_cutoff: percentageSchema,
  operations_low_stock_threshold: numberSchema,
  operations_heatmap_lookback_period: positiveIntegerSchema,
});

export const dashboardConfigSchema = financialConfigSchema
  .merge(membershipConfigSchema)
  .merge(facilityCoachConfigSchema)
  .merge(operationsConfigSchema);

export type FinancialConfigFormData = z.infer<typeof financialConfigSchema>;
export type MembershipConfigFormData = z.infer<typeof membershipConfigSchema>;
export type FacilityCoachConfigFormData = z.infer<typeof facilityCoachConfigSchema>;
export type OperationsConfigFormData = z.infer<typeof operationsConfigSchema>;
export type DashboardConfigFormData = z.infer<typeof dashboardConfigSchema>;

export const defaultFinancialConfigValues: FinancialConfigFormData = {
  financial_memberships_revenue_target_daily: '',
  financial_memberships_revenue_target_weekly: '',
  financial_memberships_revenue_target_monthly: '',
  financial_memberships_revenue_target_6months: '',
  financial_memberships_revenue_target_yearly: '',
  financial_facility_bookings_revenue_target_daily: '',
  financial_facility_bookings_revenue_target_weekly: '',
  financial_facility_bookings_revenue_target_monthly: '',
  financial_facility_bookings_revenue_target_6months: '',
  financial_facility_bookings_revenue_target_yearly: '',
  financial_products_revenue_target_daily: '',
  financial_products_revenue_target_weekly: '',
  financial_products_revenue_target_monthly: '',
  financial_products_revenue_target_6months: '',
  financial_products_revenue_target_yearly: '',
  financial_credits_revenue_target_daily: '',
  financial_credits_revenue_target_weekly: '',
  financial_credits_revenue_target_monthly: '',
  financial_credits_revenue_target_6months: '',
  financial_credits_revenue_target_yearly: '',
  financial_training_revenue_target_daily: '',
  financial_training_revenue_target_weekly: '',
  financial_training_revenue_target_monthly: '',
  financial_training_revenue_target_6months: '',
  financial_training_revenue_target_yearly: '',
  financial_large_refund_threshold: '',
};

export const defaultMembershipConfigValues: MembershipConfigFormData = {
  membership_new_member_target_daily: '',
  membership_new_member_target_weekly: '',
  membership_new_member_target_monthly: '',
  membership_new_member_target_6months: '',
  membership_new_member_target_yearly: '',
  membership_churn_rate_threshold: '',
  membership_expiry_window_1: '',
  membership_expiry_window_2: '',
  membership_expiry_window_3: '',
};

export const defaultFacilityCoachConfigValues: FacilityCoachConfigFormData = {
  facility_coach_low_utilisation_threshold: '',
  facility_coach_facility_utilisation_red_cutoff: '',
  facility_coach_facility_utilisation_amber_cutoff: '',
  facility_coach_facility_utilisation_green_cutoff: '',
  facility_coach_coach_utilisation_red_cutoff: '',
  facility_coach_coach_utilisation_amber_cutoff: '',
  facility_coach_coach_utilisation_green_cutoff: '',
  facility_coach_retention_red_cutoff: '',
  facility_coach_retention_amber_cutoff: '',
  facility_coach_retention_green_cutoff: '',
};

export const defaultOperationsConfigValues: OperationsConfigFormData = {
  operations_peak_hour_occupancy_red_cutoff: '',
  operations_peak_hour_occupancy_amber_cutoff: '',
  operations_peak_hour_occupancy_green_cutoff: '',
  operations_low_stock_threshold: '',
  operations_heatmap_lookback_period: '',
};

export const defaultDashboardConfigValues: DashboardConfigFormData = {
  ...defaultFinancialConfigValues,
  ...defaultMembershipConfigValues,
  ...defaultFacilityCoachConfigValues,
  ...defaultOperationsConfigValues,
};

// Configuration sections definition
export const configurationSections: ConfigSection[] = [
  {
    title: 'Financial Targets',
    category: 'financial',
    fields: [
      { key: 'financial_memberships_revenue_target_daily', label: 'Memberships Revenue Target (Daily)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Memberships', timePeriod: 'daily', validation: { required: false, min: 0 } },
      { key: 'financial_memberships_revenue_target_weekly', label: 'Memberships Revenue Target (Weekly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Memberships', timePeriod: 'weekly', validation: { required: false, min: 0 } },
      { key: 'financial_memberships_revenue_target_monthly', label: 'Memberships Revenue Target (Monthly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Memberships', timePeriod: 'monthly', validation: { required: false, min: 0 } },
      { key: 'financial_memberships_revenue_target_6months', label: 'Memberships Revenue Target (6 Months)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Memberships', timePeriod: '6months', validation: { required: false, min: 0 } },
      { key: 'financial_memberships_revenue_target_yearly', label: 'Memberships Revenue Target (Yearly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Memberships', timePeriod: 'yearly', validation: { required: false, min: 0 } },
      
      { key: 'financial_facility_bookings_revenue_target_daily', label: 'Facility Bookings Revenue Target (Daily)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Facility Bookings', timePeriod: 'daily', validation: { required: false, min: 0 } },
      { key: 'financial_facility_bookings_revenue_target_weekly', label: 'Facility Bookings Revenue Target (Weekly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Facility Bookings', timePeriod: 'weekly', validation: { required: false, min: 0 } },
      { key: 'financial_facility_bookings_revenue_target_monthly', label: 'Facility Bookings Revenue Target (Monthly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Facility Bookings', timePeriod: 'monthly', validation: { required: false, min: 0 } },
      { key: 'financial_facility_bookings_revenue_target_6months', label: 'Facility Bookings Revenue Target (6 Months)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Facility Bookings', timePeriod: '6months', validation: { required: false, min: 0 } },
      { key: 'financial_facility_bookings_revenue_target_yearly', label: 'Facility Bookings Revenue Target (Yearly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Facility Bookings', timePeriod: 'yearly', validation: { required: false, min: 0 } },
      
      { key: 'financial_products_revenue_target_daily', label: 'Products Revenue Target (Daily)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Products', timePeriod: 'daily', validation: { required: false, min: 0 } },
      { key: 'financial_products_revenue_target_weekly', label: 'Products Revenue Target (Weekly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Products', timePeriod: 'weekly', validation: { required: false, min: 0 } },
      { key: 'financial_products_revenue_target_monthly', label: 'Products Revenue Target (Monthly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Products', timePeriod: 'monthly', validation: { required: false, min: 0 } },
      { key: 'financial_products_revenue_target_6months', label: 'Products Revenue Target (6 Months)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Products', timePeriod: '6months', validation: { required: false, min: 0 } },
      { key: 'financial_products_revenue_target_yearly', label: 'Products Revenue Target (Yearly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Products', timePeriod: 'yearly', validation: { required: false, min: 0 } },
      
      { key: 'financial_credits_revenue_target_daily', label: 'Credits Revenue Target (Daily)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Credits', timePeriod: 'daily', validation: { required: false, min: 0 } },
      { key: 'financial_credits_revenue_target_weekly', label: 'Credits Revenue Target (Weekly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Credits', timePeriod: 'weekly', validation: { required: false, min: 0 } },
      { key: 'financial_credits_revenue_target_monthly', label: 'Credits Revenue Target (Monthly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Credits', timePeriod: 'monthly', validation: { required: false, min: 0 } },
      { key: 'financial_credits_revenue_target_6months', label: 'Credits Revenue Target (6 Months)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Credits', timePeriod: '6months', validation: { required: false, min: 0 } },
      { key: 'financial_credits_revenue_target_yearly', label: 'Credits Revenue Target (Yearly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Credits', timePeriod: 'yearly', validation: { required: false, min: 0 } },
      
      { key: 'financial_training_revenue_target_daily', label: 'Training Revenue Target (Daily)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Training', timePeriod: 'daily', validation: { required: false, min: 0 } },
      { key: 'financial_training_revenue_target_weekly', label: 'Training Revenue Target (Weekly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Training', timePeriod: 'weekly', validation: { required: false, min: 0 } },
      { key: 'financial_training_revenue_target_monthly', label: 'Training Revenue Target (Monthly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Training', timePeriod: 'monthly', validation: { required: false, min: 0 } },
      { key: 'financial_training_revenue_target_6months', label: 'Training Revenue Target (6 Months)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Training', timePeriod: '6months', validation: { required: false, min: 0 } },
      { key: 'financial_training_revenue_target_yearly', label: 'Training Revenue Target (Yearly)', type: 'pkr', unit: 'PKR', category: 'financial', subcategory: 'Training', timePeriod: 'yearly', validation: { required: false, min: 0 } },
      
      { key: 'financial_large_refund_threshold', label: 'Large Refund Threshold', type: 'pkr', unit: 'PKR', category: 'financial', validation: { required: false, min: 0 } },
    ],
  },
  {
    title: 'Membership Parameters',
    category: 'membership',
    fields: [
      { key: 'membership_new_member_target_daily', label: 'New Member Target (Daily)', type: 'number', unit: 'Count', category: 'membership', timePeriod: 'daily', validation: { required: false, min: 0 } },
      { key: 'membership_new_member_target_weekly', label: 'New Member Target (Weekly)', type: 'number', unit: 'Count', category: 'membership', timePeriod: 'weekly', validation: { required: false, min: 0 } },
      { key: 'membership_new_member_target_monthly', label: 'New Member Target (Monthly)', type: 'number', unit: 'Count', category: 'membership', timePeriod: 'monthly', validation: { required: false, min: 0 } },
      { key: 'membership_new_member_target_6months', label: 'New Member Target (6 Months)', type: 'number', unit: 'Count', category: 'membership', timePeriod: '6months', validation: { required: false, min: 0 } },
      { key: 'membership_new_member_target_yearly', label: 'New Member Target (Yearly)', type: 'number', unit: 'Count', category: 'membership', timePeriod: 'yearly', validation: { required: false, min: 0 } },
      { key: 'membership_churn_rate_threshold', label: 'Churn Rate Threshold', type: 'percentage', unit: '%', category: 'membership', validation: { required: false, min: 0, max: 100 } },
      { key: 'membership_expiry_window_1', label: 'Expiry Window 1', type: 'days', unit: 'Days', category: 'membership', validation: { required: false, min: 1 } },
      { key: 'membership_expiry_window_2', label: 'Expiry Window 2', type: 'days', unit: 'Days', category: 'membership', validation: { required: false, min: 1 } },
      { key: 'membership_expiry_window_3', label: 'Expiry Window 3', type: 'days', unit: 'Days', category: 'membership', validation: { required: false, min: 1 } },
    ],
  },
  {
    title: 'Facility & Coach Utilization',
    category: 'facility_coach',
    fields: [
      { key: 'facility_coach_low_utilisation_threshold', label: 'Low Utilisation Threshold', type: 'percentage', unit: '%', category: 'facility_coach', validation: { required: false, min: 0, max: 100 } },
      { key: 'facility_coach_facility_utilisation_red_cutoff', label: 'Facility Utilisation - Red Cutoff', type: 'percentage', unit: '%', category: 'facility_coach', subcategory: 'Facility Utilisation', validation: { required: false, min: 0, max: 100 } },
      { key: 'facility_coach_facility_utilisation_amber_cutoff', label: 'Facility Utilisation - Amber Cutoff', type: 'percentage', unit: '%', category: 'facility_coach', subcategory: 'Facility Utilisation', validation: { required: false, min: 0, max: 100 } },
      { key: 'facility_coach_facility_utilisation_green_cutoff', label: 'Facility Utilisation - Green Cutoff', type: 'percentage', unit: '%', category: 'facility_coach', subcategory: 'Facility Utilisation', validation: { required: false, min: 0, max: 100 } },
      { key: 'facility_coach_coach_utilisation_red_cutoff', label: 'Coach Utilisation - Red Cutoff', type: 'percentage', unit: '%', category: 'facility_coach', subcategory: 'Coach Utilisation', validation: { required: false, min: 0, max: 100 } },
      { key: 'facility_coach_coach_utilisation_amber_cutoff', label: 'Coach Utilisation - Amber Cutoff', type: 'percentage', unit: '%', category: 'facility_coach', subcategory: 'Coach Utilisation', validation: { required: false, min: 0, max: 100 } },
      { key: 'facility_coach_coach_utilisation_green_cutoff', label: 'Coach Utilisation - Green Cutoff', type: 'percentage', unit: '%', category: 'facility_coach', subcategory: 'Coach Utilisation', validation: { required: false, min: 0, max: 100 } },
      { key: 'facility_coach_retention_red_cutoff', label: 'Retention - Red Cutoff', type: 'percentage', unit: '%', category: 'facility_coach', subcategory: 'Retention', validation: { required: false, min: 0, max: 100 } },
      { key: 'facility_coach_retention_amber_cutoff', label: 'Retention - Amber Cutoff', type: 'percentage', unit: '%', category: 'facility_coach', subcategory: 'Retention', validation: { required: false, min: 0, max: 100 } },
      { key: 'facility_coach_retention_green_cutoff', label: 'Retention - Green Cutoff', type: 'percentage', unit: '%', category: 'facility_coach', subcategory: 'Retention', validation: { required: false, min: 0, max: 100 } },
    ],
  },
  {
    title: 'Operational Settings',
    category: 'operations',
    fields: [
      { key: 'operations_peak_hour_occupancy_red_cutoff', label: 'Peak Hour Occupancy - Red Cutoff', type: 'percentage', unit: '%', category: 'operations', subcategory: 'Peak Hour Occupancy', validation: { required: false, min: 0, max: 100 } },
      { key: 'operations_peak_hour_occupancy_amber_cutoff', label: 'Peak Hour Occupancy - Amber Cutoff', type: 'percentage', unit: '%', category: 'operations', subcategory: 'Peak Hour Occupancy', validation: { required: false, min: 0, max: 100 } },
      { key: 'operations_peak_hour_occupancy_green_cutoff', label: 'Peak Hour Occupancy - Green Cutoff', type: 'percentage', unit: '%', category: 'operations', subcategory: 'Peak Hour Occupancy', validation: { required: false, min: 0, max: 100 } },
      { key: 'operations_low_stock_threshold', label: 'Low Stock Threshold', type: 'quantity', unit: 'Quantity', category: 'operations', validation: { required: false, min: 0 } },
      { key: 'operations_heatmap_lookback_period', label: 'Heatmap Look-back Period', type: 'weeks', unit: 'Weeks', category: 'operations', validation: { required: false, min: 1 } },
    ],
  },
];
